import React, { useState, useRef, useEffect } from "react";
import { m, useMotionValue, useTransform } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { Heart, X, ExternalLink, ShoppingBag, Maximize2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CategoryManager from "../categories/CategoryManager";
import { normalizeCategory } from "@/utils/categoryUtils";
import { getColorName, getCssColor } from "@/utils/colorUtils";
import { getOptimizedImageUrl, openExternalUrl, shareContent } from "@/utils";
import OptimizedImage from "@/components/ui/OptimizedImage";

import { apiClient } from "@/api/apiClient";

const SwipeCard = React.memo(React.forwardRef(function SwipeCard({
    product,
    onSwipe,
    onImageClick,
    allProducts = [],
    onVariantClick,
    onExpandDescription,
    onVisitSite
}, ref) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isSharing, setIsSharing] = useState(false);
    const [exitX, setExitX] = useState(0);
    const [showFullDescription, setShowFullDescription] = useState(false);

    // State for fetched variants (when not available in allProducts)
    const [fetchedVariants, setFetchedVariants] = useState([]);

    const hasDiscount = product.original_price && product.original_price > product.price;

    // Find variants (same variant_group_id, excluding current product)
    // Legacy: Filter from allProducts (works for Catalog/Brand pages)
    // New: Use fetchedVariants if allProducts doesn't have them (for Swipe/Recommendation)
    const variants = React.useMemo(() => {
        if (!product.variant_group_id || product.variant_group_id.includes('single')) return [];

        // Try filtering from props first
        const propsVariants = allProducts.filter(p =>
            p.variant_group_id === product.variant_group_id
        );

        // If we found more than just self, use props
        if (propsVariants.length > 1) { // >1 because it includes self
            return propsVariants.sort((a, b) => String(a.id).localeCompare(String(b.id)));
        }

        // Otherwise use fetched variants (which might include self, need to check)
        return fetchedVariants.length > 0
            ? fetchedVariants.sort((a, b) => String(a.id).localeCompare(String(b.id)))
            : [];
    }, [product.variant_group_id, allProducts, fetchedVariants]);

    // Fetch variants on mount if needed
    useEffect(() => {
        setFetchedVariants([]); // Reset on product change

        // Only fetch if we have a group URL and props didn't provide enough variants
        // Check props count
        const propsCount = allProducts.filter(p => p.variant_group_id === product.variant_group_id).length;

        if (product.variant_group_id && !product.variant_group_id.includes('single') && propsCount <= 1) {
            apiClient.entities.Product.variants(product.variant_group_id)
                .then(setFetchedVariants)
                .catch(err => { /* console.warn("Failed to load variants for card", err) */ });
        }
    }, [product.variant_group_id, product.id /* depend on ID to reset on new card */]);

    const x = useMotionValue(0);
    const rotate = useTransform(x, [-200, 200], [-25, 25]);
    const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);
    const hasTriggeredHapticRef = useRef(false);

    // Native Android haptic feedback when crossing the 100px swipe threshold
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        hasTriggeredHapticRef.current = false;

        const unsubscribe = x.on("change", (latestX) => {
            const absX = Math.abs(latestX);
            if (absX >= 100 && !hasTriggeredHapticRef.current) {
                hasTriggeredHapticRef.current = true;
                Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
            } else if (absX < 85 && hasTriggeredHapticRef.current) {
                hasTriggeredHapticRef.current = false;
            }
        });

        return () => {
            if (typeof unsubscribe === "function") unsubscribe();
        };
    }, [x, product.id]);

    // Track unique viewed images
    const viewedIndices = useRef(new Set([0]));

    useEffect(() => {
        viewedIndices.current.add(currentImageIndex);
    }, [currentImageIndex]);

    // Expose swipe method to parent
    React.useImperativeHandle(ref, () => ({
        triggerSwipe: (direction) => {
            const targetX = direction === "right" ? 300 : -300;
            setExitX(targetX);
            onSwipe(direction, { viewedImageCount: viewedIndices.current.size });
        }
    }));

    // Effet de couleur progressive RENFORCÉ pour les indicateurs
    const likeOpacity = useTransform(x, [0, 150], [0, 1]);
    const likeScale = useTransform(x, [0, 150], [0.8, 1.3]);
    const likeBgColor = useTransform(
        x,
        [0, 40, 80, 120, 150],
        [
            "rgba(34, 197, 94, 0)",
            "rgba(34, 197, 94, 0.5)",
            "rgba(34, 197, 94, 0.8)",
            "rgba(34, 197, 94, 0.95)",
            "rgba(34, 197, 94, 1)"
        ]
    );

    const dislikeOpacity = useTransform(x, [-150, 0], [1, 0]);
    const dislikeScale = useTransform(x, [-150, 0], [1.3, 0.8]);
    const dislikeBgColor = useTransform(
        x,
        [-150, -120, -80, -40, 0],
        [
            "rgba(239, 68, 68, 1)",
            "rgba(239, 68, 68, 0.95)",
            "rgba(239, 68, 68, 0.8)",
            "rgba(239, 68, 68, 0.5)",
            "rgba(239, 68, 68, 0)"
        ]
    );

    // Overlay de couleur RENFORCÉ sur toute la carte
    const cardOverlayGreen = useTransform(
        x,
        [0, 50, 100, 150],
        [
            "rgba(34, 197, 94, 0)",
            "rgba(34, 197, 94, 0.15)",
            "rgba(34, 197, 94, 0.3)",
            "rgba(34, 197, 94, 0.4)"
        ]
    );
    const cardOverlayRed = useTransform(
        x,
        [-150, -100, -50, 0],
        [
            "rgba(239, 68, 68, 0.4)",
            "rgba(239, 68, 68, 0.3)",
            "rgba(239, 68, 68, 0.15)",
            "rgba(239, 68, 68, 0)"
        ]
    );

    const nextImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % (product.images?.length || 1));
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + (product.images?.length || 1)) % (product.images?.length || 1));
    };

    const handleDragEnd = (event, info) => {
        if (Math.abs(info.offset.x) > 100) {
            setExitX(info.offset.x > 0 ? 300 : -300);
            onSwipe(info.offset.x > 0 ? "right" : "left", { viewedImageCount: viewedIndices.current.size });
        }
    };

    const handleShare = async (e) => {
        e.stopPropagation();
        const shareUrl = product.product_url || window.location.href;
        const result = await shareContent({
            title: product.name,
            text: `Découvre ${product.name} de ${product.brand} - ${product.price}€`,
            url: shareUrl,
            dialogTitle: 'Partager cette pépite',
        });
        if (result.shared && result.method === 'clipboard') {
            alert('Lien copié dans le presse-papier!');
        }
    };

    return (
        <m.div
            style={{ x, rotate, opacity }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={handleDragEnd}
            animate={{ x: exitX }}
            transition={{ duration: 0.3 }}
            className="absolute w-full h-full cursor-grab active:cursor-grabbing touch-pan-y"
        >
            <div className="relative w-full h-full bg-white dark:bg-gray-900 rounded-xl md:rounded-3xl shadow-2xl overflow-hidden">
                {/* Overlay de couleur progressive RENFORCÉ */}
                <m.div
                    style={{ backgroundColor: cardOverlayGreen }}
                    className="absolute inset-0 pointer-events-none z-10"
                />
                <m.div
                    style={{ backgroundColor: cardOverlayRed }}
                    className="absolute inset-0 pointer-events-none z-10"
                />

                {/* Image principale */}
                <div className="relative h-[55%] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex">
                    <div className="flex-1 relative">
                        <OptimizedImage
                            src={product.images[currentImageIndex]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            width={600} // Set appropriate width for high quality swipe card
                        />

                        {/* Indicateur LIKE progressif RENFORCÉ */}
                        <m.div
                            style={{
                                opacity: likeOpacity,
                                scale: likeScale,
                                backgroundColor: likeBgColor
                            }}
                            className="absolute top-4 right-4 md:top-8 md:right-8 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-2xl font-bold text-xl md:text-2xl rotate-12 shadow-2xl z-20 flex items-center gap-2"
                        >
                            <Heart className="w-6 h-6 md:w-10 md:h-10 text-white fill-white" />
                            <span className="text-white font-black">LIKE</span>
                        </m.div>

                        {/* Indicateur NOPE progressif RENFORCÉ */}
                        <m.div
                            style={{
                                opacity: dislikeOpacity,
                                scale: dislikeScale,
                                backgroundColor: dislikeBgColor
                            }}
                            className="absolute top-4 left-4 md:top-8 md:left-8 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-2xl font-bold text-xl md:text-2xl -rotate-12 shadow-2xl z-20 flex items-center gap-2"
                        >
                            <X className="w-6 h-6 md:w-10 md:h-10 text-white" />
                            <span className="text-white font-black">NOPE</span>
                        </m.div>

                        {/* Navigation images */}
                        {product.images?.length > 1 && (
                            <>
                                <button
                                    onClick={prevImage}
                                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-12 md:h-12 bg-black/30 hover:bg-black/50 text-white rounded-full flex items-center justify-center transition-colors z-20 backdrop-blur-sm"
                                >
                                    <span className="text-xl md:text-3xl font-light pb-1">‹</span>
                                </button>
                                <button
                                    onClick={nextImage}
                                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-12 md:h-12 bg-black/30 hover:bg-black/50 text-white rounded-full flex items-center justify-center transition-colors z-20 backdrop-blur-sm"
                                >
                                    <span className="text-xl md:text-3xl font-light pb-1">›</span>
                                </button>
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                                    {product.images.map((_, idx) => (
                                        <div
                                            key={idx}
                                            className={`h-1.5 md:h-2 rounded-full transition-all ${idx === currentImageIndex ? "w-6 md:w-8 bg-white" : "w-1.5 md:w-2 bg-white/50"
                                                }`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Badges */}
                        <div className="absolute top-4 left-4 flex flex-col gap-2 z-20">
                            {product.stock_status === "limité" && (
                                <Badge className="bg-orange-500 text-white text-xs md:text-sm">Stock limité</Badge>
                            )}
                            {hasDiscount && (
                                <Badge className="bg-red-500 text-white text-xs md:text-sm">
                                    -{Math.round((1 - product.price / product.original_price) * 100)}%
                                </Badge>
                            )}
                            {(product.stock_status === 'rupture' || !product.sizes || product.sizes.length === 0 || (product.sizes.length === 1 && !product.sizes[0]) || product.sizes.every(size => typeof size === 'string' && size.includes('(Out of Stock)'))) && (
                                <Badge className="bg-gray-800 text-white border-white/20 hover:bg-gray-800 text-xs md:text-sm">
                                    Rupture de stock
                                </Badge>
                            )}
                        </div>

                        <div className="absolute bottom-4 right-4 flex gap-2 z-20">
                            <Button
                                size="icon"
                                variant="secondary"
                                className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                onClick={handleShare}
                            >
                                <Share2 className="w-3 h-3 md:w-4 md:h-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="secondary"
                                className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                onClick={() => onImageClick({ url: product.images[currentImageIndex], index: currentImageIndex })}
                            >
                                <Maximize2 className="w-3 h-3 md:w-4 md:h-4" />
                            </Button>
                        </div>
                    </div>


                </div>

                {/* Informations produit */}
                <div className="h-[45%] p-3 md:p-4 flex flex-col justify-between overflow-hidden">
                    <div className="flex-1 overflow-y-auto touch-pan-y">
                        <div className="flex items-start justify-between mb-1 md:mb-2">
                            <div className="flex-1">
                                <h3 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white mb-0.5 md:mb-1 leading-tight">{product.name}</h3>
                                <span
                                    className="inline-block text-xs md:text-sm text-amber-600 dark:text-amber-400 font-medium hover:underline cursor-pointer relative z-50"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                        if (product.brand_slug) {
                                            window.location.hash = `#/brand/${product.brand_slug}`;
                                        }
                                    }}
                                >{product.brand}</span>
                            </div>
                            <div className="text-right ml-2">
                                <div className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{product.price}€</div>
                                {hasDiscount && (
                                    <div className="text-xs md:text-sm text-gray-400 line-through">
                                        {product.original_price}€
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Variants (horizontal row) */}
                        {variants.length > 0 && (
                            <div className="mb-2 md:mb-3">
                                <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">Variantes :</p>
                                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                                    {variants.map(variant => {
                                        const variantImage = variant.images?.[0];
                                        // Skip variants without images
                                        if (!variantImage) return null;

                                        const isActive = variant.id === product.id;
                                        return (
                                            <div
                                                key={variant.id}
                                                className={`relative w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all ${isActive
                                                    ? 'cursor-default border-2 border-amber-500 ring-2 ring-amber-300 dark:ring-amber-900'
                                                    : 'cursor-pointer border-2 border-transparent hover:border-amber-300 opacity-80 hover:opacity-100'
                                                    }`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!isActive && onVariantClick) onVariantClick(variant);
                                                }}
                                                title={getColorName(variant.colors?.[0]) || variant.name}
                                            >
                                                <OptimizedImage
                                                    src={variantImage}
                                                    alt={variant.name}
                                                    width={48}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Description avec voir plus */}
                        {product.description && (
                            <div className="mb-2 md:mb-3">
                                <p className={`text-xs md:text-sm text-gray-600 dark:text-gray-300 ${!showFullDescription ? 'line-clamp-2' : ''}`}>
                                    {product.description}
                                </p>
                                {product.description.length > 100 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const newState = !showFullDescription;
                                            setShowFullDescription(newState);
                                            if (newState && onExpandDescription) {
                                                onExpandDescription(product.id);
                                            }
                                        }}
                                        className="text-[10px] md:text-xs text-amber-600 dark:text-amber-400 hover:text-amber-500 font-medium mt-1"
                                    >
                                        {showFullDescription ? 'Voir moins' : 'Voir plus'}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Catégories personnalisées */}
                        <div className="mb-2 md:mb-3">
                            <CategoryManager
                                productId={product.id}
                                size="sm"
                            />
                        </div>

                        {/* Matière */}
                        {product.materials && (
                            <div className="mb-2 md:mb-3">
                                <div className="flex items-start gap-2">
                                    <span className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-medium">Matière:</span>
                                    <p className="text-[10px] md:text-xs text-gray-700 dark:text-gray-300">{product.materials}</p>
                                </div>
                            </div>
                        )}

                        {/* Tailles et couleurs */}
                        <div className="flex gap-4 mb-2 md:mb-3">
                            {product.sizes && product.sizes.length > 0 && (
                                <div className="flex items-start gap-2 flex-wrap">
                                    <span className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-medium shrink-0">Tailles:</span>
                                    <div className="flex gap-1 flex-wrap">
                                        {product.sizes.map((size, idx) => (
                                            <Badge key={idx} variant="outline" className="text-[10px] md:text-xs px-1.5 py-0.5 dark:border-gray-700 dark:text-gray-300">
                                                {size}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>


                    </div>

                    {/* Bouton voir le produit */}
                    {product.product_url && (
                        <a
                            href={product.product_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onVisitSite) onVisitSite(product.id);
                                if (Capacitor.isNativePlatform()) {
                                    openExternalUrl(product.product_url, e);
                                }
                            }}
                        >
                            <Button className="w-full h-9 md:h-10 text-sm md:text-base bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md hover:shadow-amber-500/20">
                                <ShoppingBag className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                                Voir sur le site
                                <ExternalLink className="w-3 h-3 md:w-4 md:h-4 ml-2" />
                            </Button>
                        </a>
                    )}
                </div>
            </div>
        </m.div>
    );
}));

export default SwipeCard;